/**
 * The box every ledger list row is drawn in, shared by `LedgerEntryItem`,
 * `LedgerEntryItemCombined`, `PaymentItem` and their placeholder,
 * `TransactionListSkeleton`.
 *
 * `LedgerListTransition` fades the placeholder out over the loaded rows, so
 * for that moment both are on screen in the same place. A placeholder row
 * whose box differs from a loaded row's (padding, wrap point, gaps, divider,
 * separator) shows as a doubled row and a height jump when it goes. Importing
 * these strings keeps the two in step.
 *
 * Each value is a whole literal class list, so Tailwind finds every class.
 * Hover, selected state, `cursor-pointer` and the vertical alignment of the
 * inner row stay on each row. The placeholder has no hover, selected state or
 * `cursor-pointer`, and aligns its inner row to the top as the single-entry
 * rows do, so its bars stay level with their text while the two crossfade.
 * The combined row centres its items instead.
 */

/** The outer wrapper of a row: the rounded hover surface. */
export const LEDGER_ROW_SHELL_CLASSES = "rounded-lg px-2" as const;

/** The padded box inside the wrapper that holds the row's content. */
export const LEDGER_ROW_BOX_CLASSES =
  "mx-[-12px] px-4 py-3 pb-4 lg:py-4" as const;

/**
 * The row's content line: below md it wraps, so the info side takes the full
 * width and the amount and date move onto a line of their own; from md it is
 * one line.
 */
export const LEDGER_ROW_LAYOUT_CLASSES =
  "flex flex-wrap md:flex-nowrap justify-between gap-x-4 gap-y-1 md:gap-4 whitespace-nowrap" as const;

/**
 * The short vertical rule between the amount and the date, shown from xl,
 * where the two sit on one line. Its `mx-6` sets where the date starts, so a
 * placeholder separator that differs puts its date bar beside the loaded
 * date instead of over it while the two crossfade. The combined row stretches
 * its rule to the row's height and keeps its own.
 */
export const LEDGER_ROW_SEPARATOR_CLASSES =
  "h-6 mx-6 w-px bg-gray-300 hidden xl:block opacity-50" as const;

/** The line under each row. */
export const LEDGER_ROW_DIVIDER_CLASSES =
  "h-px bg-border shadow-xs opacity-50" as const;
